import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from '../i18n';

const StarsResultModal = ({ stars, percentage, earnedPoints, totalPoints, onSubmit, onSkip }) => {
  const { t } = useTranslation();
  const earned = Math.max(0, Math.min(5, stars ?? 0));
  const hadExercises = totalPoints > 0;

  const [lessonRating, setLessonRating] = useState(0);
  const [comment, setComment] = useState('');

  const starsNoun = (n) => (n === 1 ? t('modals.starsOne') : t('modals.starsMany'));

  const title = !hadExercises
    ? t('modals.starsLessonDone')
    : earned === 5 ? t('modals.starsPerfect')
    : earned === 4 ? t('modals.starsGreat')
    : earned === 3 ? t('modals.starsGood')
    : earned === 2 ? t('modals.starsNice')
    : earned === 1 ? t('modals.starsProgress')
    : t('modals.starsNextTime');

  const handleSubmit = () => {
    onSubmit({ rating: lessonRating, comment });
    setLessonRating(0);
    setComment('');
  };

  const handleSkip = () => {
    onSkip();
    setLessonRating(0);
    setComment('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="w-full max-w-sm rounded-2xl p-5 sm:p-8 bg-white shadow-2xl my-auto max-h-[calc(100vh-2rem)] overflow-y-auto animate-in fade-in zoom-in duration-300">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">{title}</h2>

          {hadExercises && (
            <>
              <div className="flex justify-center gap-1 mb-3 sm:mb-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`w-9 h-9 sm:w-11 sm:h-11 transition-all ${
                      i < earned
                        ? 'text-yellow-400 fill-yellow-400 drop-shadow'
                        : 'text-gray-200 fill-gray-200'
                    }`}
                  />
                ))}
              </div>

              <p className="text-gray-600 text-sm sm:text-base mb-1">
                {t('modals.starsCorrect')}: <span className="font-semibold">{t('modals.starsCorrectOf', { earned: earnedPoints, total: totalPoints })}</span> ({percentage}%)
              </p>

              {earned > 0 && (
                <p className="text-sm sm:text-base font-medium mb-4 sm:mb-5" style={{ color: '#2A4D25' }}>
                  {t('modals.starsToProfile', { count: earned, noun: starsNoun(earned) })}
                </p>
              )}
              {earned === 0 && <div className="mb-4 sm:mb-5" />}

              <div className="border-t border-gray-200 my-3 sm:my-4" />
            </>
          )}

          <p className="text-base sm:text-lg font-semibold text-gray-800 mb-1">{t('modals.starsRateLesson')}</p>
          <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">
            {t('modals.starsRateSubtitle')}
          </p>

          <div className="flex justify-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setLessonRating(s)}
                className="transition-transform hover:scale-110 active:scale-95"
                aria-label={`${s} ${starsNoun(s)}`}
              >
                <Star
                  className={`w-9 h-9 sm:w-11 sm:h-11 ${
                    s <= lessonRating
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300 fill-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('modals.starsCommentPlaceholder')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:outline-none mb-4"
            style={{ '--tw-ring-color': '#2A4D25' }}
            rows="2"
          />

          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={handleSkip}
              className="flex-1 py-3 rounded-xl font-medium text-sm sm:text-base bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all active:scale-[0.98]"
            >
              {t('modals.starsSkip')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={lessonRating === 0}
              className={`flex-1 py-3 rounded-xl font-medium text-sm sm:text-base text-white transition-all active:scale-[0.98] ${
                lessonRating === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              style={{ backgroundColor: '#2A4D25' }}
            >
              {t('modals.starsSubmit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarsResultModal;
